import {
  IconBuildingStore,
  IconKey,
  IconLogout,
  IconPlus,
  IconShieldLock,
} from "@tabler/icons-react";
import type { ReactNode } from "react";
import { NavLink } from "react-router-dom";

import { useSession } from "../../auth/SessionProvider";
import { ThemeSwitch } from "./ThemeSwitch";

interface NavItemProps {
  to: string;
  /** Match this destination exactly (the home route would match everything). */
  end?: boolean;
  icon: ReactNode;
  label: string;
  /** One short line saying what the destination is for. */
  detail: string;
}

/** One navigation entry: an icon beside a word, never an icon alone. */
function NavItem({ to, end, icon, label, detail }: NavItemProps): JSX.Element {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `sidebar__item${isActive ? " sidebar__item--active" : ""}`
      }
    >
      <span className="sidebar__item-icon" aria-hidden="true">
        {icon}
      </span>
      <span className="sidebar__item-text">
        <span className="sidebar__item-label">{label}</span>
        <span className="sidebar__item-detail">{detail}</span>
      </span>
    </NavLink>
  );
}

/**
 * The left column: who we are, where you can go, and who you are.
 *
 * Every entry names itself — an icon only ever sits beside its word. Four
 * entries, because this product has four things a person does: look at what
 * clients gave us, add one, carry a key into a workspace, and (for an admin)
 * approve people and watch the storage.
 *
 * Returns:
 *     The navigation sidebar.
 */
export function Sidebar(): JSX.Element {
  const { me, signOut } = useSession();

  return (
    <aside className="sidebar">
      <NavLink to="/" className="sidebar__brand" end>
        <span className="sidebar__brand-text">
          <span className="sidebar__brand-name">Data Console</span>
          <span className="sidebar__brand-org">Neuralk</span>
        </span>
      </NavLink>

      <nav className="sidebar__nav" aria-label="Main">
        <NavItem
          to="/"
          end
          icon={<IconBuildingStore size={17} stroke={1.75} />}
          label="Clients"
          detail="Whose data we hold, and where each deal stands"
        />
        <NavItem
          to="/new"
          icon={<IconPlus size={17} stroke={1.75} />}
          label="Add a dataset"
          detail="Four questions, and the file goes up behind them"
        />
        <NavItem
          to="/account"
          icon={<IconKey size={17} stroke={1.75} />}
          label="Your account"
          detail="Your keys and your code"
        />
        {me.is_admin ? (
          <NavItem
            to="/admin"
            icon={<IconShieldLock size={17} stroke={1.75} />}
            label="Admin"
            detail="People, the audit, storage, the pause switch"
          />
        ) : null}
      </nav>

      <div className="sidebar__identity">
        <p className="sidebar__who">
          <span className="sidebar__who-name">
            {me.display_name ?? me.username}
          </span>
          {me.email ? (
            <span className="sidebar__who-mail">{me.email}</span>
          ) : null}
        </p>
        <ThemeSwitch />
        <button type="button" className="sidebar__signout" onClick={signOut}>
          <IconLogout size={14} aria-hidden="true" />
          Sign out
        </button>
      </div>
    </aside>
  );
}
